// Chakra imports
import { SimpleGrid, Text, Button, useColorModeValue, Spinner, Center, Box, Flex, Icon, Heading, Alert, AlertIcon, AlertDescription, HStack, Badge, VStack, Progress, Collapse, List, ListItem, ListIcon, Divider } from "@chakra-ui/react";
import { CheckCircleIcon, WarningIcon, InfoOutlineIcon, ChevronRightIcon, ChevronDownIcon } from "@chakra-ui/icons";
// Custom components
import Card from "components/card/Card.js";
import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import Information from "views/admin/profile/components/Information";

import constants from "providers/Constants";
import useAuthentication from "providers/Authentication";

// Assets
export default function AddressInformation(props) {

  const authentication = useAuthentication();
  const { t } = useTranslation();

  const { user, profile, uploads, countries, ...rest } = props;

  // Location capture state
  const [location, setLocation] = useState(profile?.address_upload_location || {});
  const [capturing, setCapturing] = useState(false);

  const [error, setError] = React.useState([]);
  const [loading, setLoading] = useState(false);
  const [address_verification, setAddressVerification] = useState([]);

  // Chakra Color Mode
  const textColorPrimary = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = "gray.400";
  const cardShadow = useColorModeValue(
    "0px 18px 40px rgba(112, 144, 176, 0.12)",
    "unset"
  );

  useEffect(() => {
    if (profile?.address_upload_country) { getDocumentCategories(profile?.address_upload_country); }
  }, [user, profile, uploads]);


  const innerBg = useColorModeValue('white', 'navy.700');
  const locationBg = useColorModeValue('gray.50', 'navy.800');

  // Capture location
  async function captureLocation() {
    try {
      setCapturing(true);
      const captured = await constants.getCurrentPosition();
      if (!captured || !captured.latitude || !captured.longitude) {
        setCapturing(false);
        return;
      }
      setLocation(captured);
      setCapturing(false);
      updateProfile('address_upload_location', captured);
    } catch { setCapturing(false); }
  }

  return (
    <Card mb={{ base: "0px", "2xl": "20px" }} {...rest}>
      {loading ? (
        <Center w='100%' minH={rest?.minH || '220px'}>
          <Spinner size="xl" />
        </Center>
      ) : (
        <>
          {/* Verification Status Banner */}
          <StatusBanner
            status={profile?.address_upload_status}
            message={profile?.address_upload_status_message}
            onAction={submitDocument}
            submittedAt={profile?.address_upload_timestamp}
            checklist={{
              country: !!profile?.address_upload_country,
              type: !!profile?.address_upload_type,
              proof: !!profile?.address_upload_photo,
              location: !!profile?.address_upload_location && !!profile?.address_upload_location?.latitude && !!profile?.address_upload_location?.longitude,
            }}
          />
          <SimpleGrid columns='2' gap='20px'>
            <Information
              boxShadow={cardShadow}
              title={t('Address Verification Country')}
              select={true}
              disabled={isLocked(profile?.address_upload_status)}
              select_array={countries}
              value={`${profile?.address_upload_country || t('N/A')}`}
              onChange={e => {
                profile['address_upload_country'] = e;
                getDocumentCategories(e);
                updateProfile('address_upload_country', e);
              }}
            />
            {address_verification.length > 0 && (
              <Information
                boxShadow={cardShadow}
                title={t('Document Type')}
                select={true}
                disabled={isLocked(profile?.address_upload_status)}
                select_array={address_verification}
                hint={t('HINT: (UB - Utility Bill), (BS - Bank Statement)')}
                value={`${profile?.address_upload_type || t('N/A')}`}
                onChange={e => {
                  profile['address_upload_type'] = e;
                  updateProfile('address_upload_type', e);
                }}
              />
            )}
            <Information
              boxShadow={cardShadow}
              title={t('Address Proof Upload')}
              upload={true}
              disabled={isLocked(profile?.address_upload_status)}
              upload_array={uploads}
              hint={t('HINT: Select file from your uploads on BlockMap. Use the file uploader tool to upload files')}
              value={`${profile?.address_upload_photo || t('N/A')}`}
              onChange={e => {
                profile['address_upload_photo'] = e;
                updateProfile('address_upload_photo', e);
              }}
            />

            {/* Location capture */}
            <Box gridColumn="1 / span 2" my={2}>
              <Card mb={{ base: "0px", "2xl": "0px" }} boxShadow={cardShadow}>
                <Flex direction="column" gap={4} p={4} bg={innerBg} borderRadius="12px">
                  <Heading size="sm">{t('Use device location')}</Heading>
                  <Text color={textColorSecondary}>{t('Capture coordinates to support verification.')}</Text>
                  <Button
                    leftIcon={
                      <Icon
                        as={() => (
                          (location && location.latitude)
                            ? (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M17.65 6.35A7.95 7.95 0 0 0 12 4V1L7 6l5 5V8a5 5 0 1 1-5 5H5a7 7 0 1 0 12.65-6.65z" fill="currentColor" />
                              </svg>
                            )
                            : (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M11 2h2v3a7 7 0 0 1 6 6h3v2h-3a7 7 0 0 1-6 6v3h-2v-3a7 7 0 0 1-6-6H2v-2h3a7 7 0 0 1 6-6V2zm1 5a5 5 0 1 0 0 10 5 5 0 0 0 0-10z" fill="currentColor" />
                              </svg>
                            )
                        )}
                      />
                    }
                    onClick={captureLocation}
                    isLoading={capturing}
                    variant='brand'
                    isDisabled={isLocked(profile?.address_upload_status)}
                    width="220px"
                  >
                    {location && location.latitude ? t('Refresh location') : t('Capture location')}
                  </Button>

                  {location && location.latitude && (
                    <Box borderRadius="12px" borderWidth="1px" p={4} bg={locationBg}>
                      <Flex justify="space-between">
                        <Box>
                          <Text fontSize='xs' color='gray.500'>{t('LATITUDE')}</Text>
                          <Text fontWeight='bold' fontSize='lg'>{location.latitude}</Text>
                        </Box>
                        <Box>
                          <Text fontSize='xs' color='gray.500'>{t('LONGITUDE')}</Text>
                          <Text fontWeight='bold' fontSize='lg'>{location.longitude}</Text>
                        </Box>
                        <Box>
                          <Text fontSize='xs' color='gray.500'>{t('ACCURACY')}</Text>
                          <Text fontWeight='bold' fontSize='lg'>
                            {t('± {{meters}} m', { meters: Math.round(location.accuracy || 0) })}
                          </Text>
                        </Box>
                      </Flex>
                      <Text fontSize='sm' color='gray.500' mt={3}>
                        {t('Captured {{datetime}}', { datetime: new Date(location.timestamp).toLocaleString() })}
                      </Text>
                      {capturing && (
                        <Text mt={3} fontSize='sm' color='gray.500'>
                          {t('Looking up address...')}
                        </Text>
                      )}
                      {/* Address details display */}
                      {/* {(location.formatted || location.address || location.city || location.state || location.postal_code || location.country) && (
                        <Box mt={3} p={3} borderRadius='md' bg={innerBg}>
                          <Heading size='xs' mb={2}>{t('Address details')}</Heading>
                          {location.formatted && (
                            <Text fontSize='sm' color={textColorPrimary}>{location.formatted}</Text>
                          )}
                          <Flex mt={location.formatted ? 2 : 0} wrap='wrap' rowGap={1} columnGap={6}>
                            {location.address && (
                              <Text fontSize='sm' color='gray.600'><strong>{t('Street')}:</strong> {location.address}</Text>
                            )}
                            {location.city && (
                              <Text fontSize='sm' color='gray.600'><strong>{t('City')}:</strong> {location.city}</Text>
                            )}
                            {location.state && (
                              <Text fontSize='sm' color='gray.600'><strong>{t('State')}:</strong> {location.state}</Text>
                            )}
                            {location.postal_code && (
                              <Text fontSize='sm' color='gray.600'><strong>{t('Postal Code')}:</strong> {location.postal_code}</Text>
                            )}
                            {location.country && (
                              <Text fontSize='sm' color='gray.600'><strong>{t('Country')}:</strong> {location.country}</Text>
                            )}
                          </Flex>
                        </Box>
                      )} */}
                      <Box mt={3} p={3} borderRadius='md' bg={innerBg}>
                        <Text fontSize='sm' color='gray.500'>
                          {t('Your location stays encrypted with your KYC.')}
                        </Text>
                      </Box>
                    </Box>
                  )}
                </Flex>
              </Card>
            </Box>

            {/* Validation Errors */}
            {error.length > 0 &&
              error.map((err, index) => (
                <Text
                  key={index}
                  mb='10px'
                  ms='4px'
                  color={'red'}
                  fontWeight='400'
                  fontSize='md'>
                  * {err.msg}
                </Text>
              ))}
          </SimpleGrid>
        </>
      )}
    </Card>
  );

  // Get document categories
  async function getDocumentCategories(country_iso_code_2) {
    setLoading(true);
    authentication.getVerificationTypes(country_iso_code_2, (error, response) => {
      setLoading(false);
      if (error) {
        constants.showToast(
          "error",
          t('FAILED'),
          error?.message || t('Snap! Something went wrong, Try again')
        );
      } else {
        const address_verification_map = response?.address_verification?.map(entry => entry.id) || [];
        setAddressVerification(address_verification_map);
      }
    });
  }

  // Update profile
  async function updateProfile(key, value) {
    setLoading(true);
    const data = {
      [key]: value,
    }
    authentication.updateUser(data, (error, data) => {
      setLoading(false);
      if (error) {
        constants.showToast(
          "error",
          t('FAILED'),
          error?.message || t('Snap! Something went wrong, Try again')
        );
      } else {
        constants.showToast(
          "success",
          t('SUCCESS'),
          t('Profile updated successfully')
        );
      }
    });
  }

  // Submit document
  async function submitDocument() {
    const isValid = validateSubmitAddress();
    if (!isValid) return;
    setLoading(true);
    authentication.updateUser({
      action: 'address_verification', address_upload_type: profile?.address_upload_type, address_upload_country: profile?.address_upload_country, address_upload_timestamp: Date.now(), address_upload_verified: false,
      address_upload_photo: profile?.address_upload_photo, address_upload_status: 'pending', address_upload_status_message: 'Address verification is pending approval!', address_upload_location: profile?.address_upload_location,
    }, (error, data) => {
      setLoading(false);
      if (error) {
        setError(error.errors);
        constants.showToast(
          "error",
          t('FAILED'),
          error?.message || t('Snap! Something went wrong, Try again')
        );
      } else {
        constants.showToast(
          "success",
          t('SUCCESS'),
          t('Verification document submitted successfully!')
        );
        window.location.reload();
      }
    });
  }

  // Validate submit address
  function validateSubmitAddress() {
    const checks = [
      [!!profile?.address_upload_type, t('Please select address type!')],
      [!!profile?.address_upload_country, t('Please select address country!')],
      [!!profile?.address_upload_photo, t('Please upload address image!')],
      [!!profile?.address_upload_location, t('Please capture address location!')],
      [!!profile?.address_upload_location?.latitude, t('Please capture address location!')],
      [!!profile?.address_upload_location?.longitude, t('Please capture address location!')],
    ];
    const errors = checks.filter(([ok]) => !ok).map(([, msg]) => ({ msg }));
    if (errors.length) {
      setError(errors);
      constants.showToast(
        "error",
        t('FAILED'),
        errors.map(e => e.msg).join('\n')
      );
      return false;
    }
    return true;
  }
}

// Check list item
function ChecklistItem({ ok, label }) {
  return (
    <ListItem>
      <HStack>
        <ListIcon as={ok ? CheckCircleIcon : WarningIcon} color={ok ? 'green.400' : 'orange.300'} />
        <Text>{label}</Text>
      </HStack>
    </ListItem>
  );
}

// Is locked
function isLocked(status) {
  const s = (status || '').toLowerCase();
  return s === 'verified' || s === 'pending';
}


// Status banner
function StatusBanner({ status, message, onAction, submittedAt, checklist }) {
  const { t } = useTranslation();
  const s = (status || '').toLowerCase();
  const meta = (() => {
    switch (s) {
      case 'verified':
        return { label: t('Verified'), chakraStatus: 'success', colorScheme: 'green', description: message ? t(message) : t('Your address is verified. No further action required.') };
      case 'pending':
        return { label: t('Pending'), chakraStatus: 'warning', colorScheme: 'orange', description: message ? t(message) : t('Your address proof is under review.') };
      case 'rejected':
      case 'failed':
        return { label: t('Rejected'), chakraStatus: 'error', colorScheme: 'red', description: message ? t(message) : t('We could not verify your address. Please review and resubmit.'), actionLabel: t('Resubmit'), actionLabelArrow: t('Resubmit →') };
      default:
        return { label: t('Not Submitted'), chakraStatus: 'info', colorScheme: 'blue', description: message ? t(message) : t('Upload an address proof and capture your location to start verification.'), actionLabel: t('Submit'), actionLabelArrow: t('Submit →') };
    }
  })();

  const progress = s === 'verified' ? 100 : s === 'pending' ? 66 : s === 'rejected' || s === 'failed' ? 40 : 15;
  const [expanded, setExpanded] = React.useState(false);
  const [showTips, setShowTips] = React.useState(false);
  const textColorSecondary = 'gray.400';

  return (
    <Box mb={4}>
      <Alert status={meta.chakraStatus} variant="left-accent" borderRadius="10px" alignItems="flex-start" py={4} px={4}>
        <AlertIcon />
        <Box flex="1">
          <Flex justify="space-between" align="center" gap={3} w="100%">
            <HStack spacing={3} align="center">
              <Badge colorScheme={meta.colorScheme} borderRadius="full" px={2} py={0.5}>{meta.label}</Badge>
            </HStack>
            {meta.actionLabel && (
              <Button size="sm" colorScheme={meta.colorScheme} onClick={onAction}>
                {meta.actionLabelArrow || meta.actionLabel}
              </Button>
            )}
          </Flex>
          <Flex justify="space-between" align="center" gap={3} mt={1} w="100%">
            <AlertDescription color={textColorSecondary} fontSize="sm" m={0}>
              {meta.description}
              {constants.formatDate(submittedAt) && (
                <Text as="span" ml={2} fontSize="xs" color={textColorSecondary}>• {t('Last submitted:')} {constants.formatDate(submittedAt)}</Text>
              )}
            </AlertDescription>
            <Button variant="ghost" size="sm" rightIcon={expanded ? <ChevronDownIcon /> : <ChevronRightIcon />} onClick={() => setExpanded(v => !v)}>
              {t('Details')}
            </Button>
          </Flex>
          <Collapse in={expanded} animateOpacity>
            <VStack align="stretch" mt={3} spacing={3}>
              <Progress value={progress} colorScheme={meta.colorScheme} borderRadius="full" size="xs" />
              <List spacing={1} fontSize="sm" color={textColorSecondary}>
                <ChecklistItem ok={checklist?.country} label={t('Verification Country')} />
                <ChecklistItem ok={checklist?.type} label={t('Document Type')} />
                <ChecklistItem ok={checklist?.proof} label={t('Proof Uploaded')} />
                <ChecklistItem ok={checklist?.location} label={t('Location Captured')} />
              </List>
              <Divider />
              <HStack fontSize="sm" color={textColorSecondary} justify="space-between">
                <HStack>
                  <InfoOutlineIcon />
                  <Text>{t('Tip: capture location where the document applies (home or office).')}</Text>
                </HStack>
                <Button variant="link" colorScheme={meta.colorScheme} size="sm" onClick={() => setShowTips(v => !v)}>
                  {showTips ? t('Hide tips') : t('Show tips')}
                </Button>
              </HStack>
              <Collapse in={showTips} animateOpacity>
                <Text fontSize="sm" color={textColorSecondary}>
                  {t('Ensure your document shows full name and address clearly. Use good lighting and avoid glare.')}
                </Text>
              </Collapse>
            </VStack>
          </Collapse>
        </Box>
      </Alert>
    </Box>
  );
}
